/**
 * A composição da tela de atendimento (C24, §11.2) — uma requisição pinta
 * as três colunas: a conversa (coluna central), as mensagens paginadas e o
 * contexto comercial (painel direito), com as `capabilities` do canal.
 *
 * Os campos vêm do inventário da A07 (`docs/prototipo/inventario-de-campos.md`)
 * ATÉ ONDE as fontes de hoje alcançam — a Armadilha da C24 é devolver a
 * entidade inteira das projeções, e a contrapartida dela é: campo sem fonte
 * ainda NÃO entra no contrato fingindo existir. As famílias que faltam têm
 * dono nomeado: read models de CRM (D01) enriquecem oportunidade sem ir ao
 * Twenty; anexos com URL assinada vêm junto do envio (C22 Fora); saúde de
 * canal e bloqueio de envio são a D14; vincular manualmente é a D02/D04.
 *
 * `stale` e `projetado_em` entram DESDE JÁ (Técnica da task): acrescentar
 * depois obriga a mexer em todo consumidor, e é o que a §14.3 exige para o
 * modo degradado.
 */

import { z } from 'zod';
import { ValoresDeCamposSchema } from './campos-personalizados.js';
import { IdSchema } from './comum.js';
import { InstanteSchema } from './valores.js';
import { AnexoSchema } from './anexos.js';

/** Vocabulário do conversation-domain (C02) — o inventário chama `em_espera` de "adiada"; o canônico vence. */
export const SituacaoDaConversaSchema = z.enum(['aberta', 'pendente', 'resolvida', 'em_espera']);

const ReferenciaExternaSchema = z
  .string()
  .describe('ID do registro na engine — campo de REFERÊNCIA (§3.2), nunca o id principal.');

export const ConversaCompostaSchema = z.object({
  conversa: z.object({
    id: IdSchema,
    situacao: SituacaoDaConversaSchema,
    prioridade: z.enum(['baixa', 'media', 'alta', 'urgente']).nullable(),
    /** O canal fino da matriz da A03 (C03) — nunca o `Channel::*` cru da engine. */
    canal: z.string().nullable(),
    /**
     * A caixa (número, conta) por onde a conversa chegou, como referência do
     * atendimento. É a chave do roteamento de funil: com ela a tela sugere o
     * funil da regra do número ao criar oportunidade à mão.
     */
    caixa_referencia_externa: ReferenciaExternaSchema,
    /**
     * O NOME da caixa, como aparece no Atendimento (#463). Com dois números de
     * WhatsApp na mesma organização, o canal sozinho não diz por qual a
     * conversa chegou nem por qual a resposta sai. `null` quando a caixa ainda
     * não foi projetada: a tela simplesmente não mostra.
     */
    caixa_nome: z.string().nullable(),
    nao_lidas: z.number().int(),
    /**
     * Quem atende, com NOME.
     *
     * O nome vem da projeção, e não de uma chamada ao motor a cada leitura:
     * a tela é servida só da projeção de propósito (§14.3), e resolver o nome
     * ao vivo faria a coluna "Responsável" sumir justamente quando o motor
     * cai — que parece conversa sem dono.
     *
     * `nome` pode ser nulo com `referencia_externa` presente: é a conversa
     * atribuída antes de esta versão existir, e a tela mostra a referência em
     * vez de mentir um nome.
     */
    responsavel: z
      .object({
        referencia_externa: ReferenciaExternaSchema,
        nome: z.string().nullable(),
        /**
         * A foto de perfil de quem atende (061), ou nulo. O agente do motor de
         * conversa vira pessoa pelo vínculo (`conversa_agente_source_id`); sem
         * vínculo, ou sem foto, nulo, e a tela mostra as iniciais.
         */
        foto_url: z.string().nullable(),
      })
      .nullable(),
    /**
     * As etiquetas, pelo TÍTULO. A cor vem do catálogo (`GET /v1/etiquetas`).
     *
     * Separadas porque a cor é da etiqueta, não da conversa: junta, trocar a
     * cor de uma etiqueta exigiria reprojetar toda conversa que a tem, e até
     * lá a mesma etiqueta sairia de duas cores em linhas vizinhas da fila.
     */
    etiquetas: z.array(z.string()),
    aberta_em: InstanteSchema,
    ultima_mensagem_em: InstanteSchema.nullable(),
    projetado_em: InstanteSchema,
    /** §14.3: projeção defasada além do limiar — a tela avisa "dado defasado". */
    stale: z.boolean(),
  }),

  /**
   * O bloco que vem antes de todos no painel direito (A07 §5.1): sem ele o
   * painel fica em branco e silencioso — a armadilha nomeada na H04.
   */
  vinculo: z.object({
    estado: z.enum(['vinculado', 'em_revisao', 'sem_vinculo']),
    /** `conversation_links.link_method` (C18). */
    metodo: z.enum(['automatico', 'manual', 'regra']).nullable(),
    vinculado_em: InstanteSchema.nullable(),
    /** `false` até o endpoint manual existir (D02/D04) — a tela esconde a ação, não a inventa. */
    pode_vincular_manualmente: z.boolean(),
  }),

  cliente: z
    .object({
      id: IdSchema,
      nome_exibicao: z.string().nullable(),
      telefone_exibicao: z.string().nullable(),
      email: z.string().nullable(),
      documento: z.object({
        /** Só a EXISTÊNCIA — o número, mesmo mascarado, exige o step-up da B17 (A07 D14f/D15f). */
        presente: z.boolean(),
      }),
      /** Os campos personalizados do cliente (#568), para preencher sem sair da conversa. */
      campos: ValoresDeCamposSchema,
    })
    .nullable(),

  oportunidades: z.array(
    z.object({
      id: IdSchema,
      titulo: z.string().nullable(),
      etapa: z.string().nullable(),
      /** O nome que se lê ("Novo · Agendamento"), pronto; sem ele, a tela mostra a `etapa`. */
      etapa_nome: z.string().nullable().optional(),
      /**
       * A etapa é de ganho ou de perda: o negócio acabou. O agendamento não
       * oferece o card encerrado (a consulta ficaria num negócio fechado).
       */
      encerrada: z.boolean().optional(),
      valor: z.object({ quantia_micros: z.number(), moeda: z.string() }).nullable(),
      responsavel: z.object({ referencia_externa: ReferenciaExternaSchema }).nullable(),
      atualizada_em: InstanteSchema.nullable(),
      /** `true` = o Twenty não respondeu agora; só o vínculo (id) é garantido (§14.3). */
      stale: z.boolean(),
    }),
  ),

  /**
   * As tarefas em aberto do cliente que são de quem lê ou de ninguém (#641),
   * do prazo mais próximo para o mais distante; sem prazo, no fim.
   *
   * Tarefa de outra pessoa não vem, mesmo para quem enxerga a revenda inteira:
   * a pergunta da aba é "o que EU tenho para fazer com este cliente", e não a
   * agenda da equipe. Vazio sem cliente vinculado.
   */
  tarefas_futuras: z.object({
    itens: z.array(
      z.object({
        referencia_externa: ReferenciaExternaSchema,
        titulo: z.string().nullable(),
        prazo_em: InstanteSchema.nullable(),
        /** `true` = sem responsável; `false` = de quem lê. */
        sem_responsavel: z.boolean(),
      }),
    ),
    /**
     * `true` = havia o que perguntar e o cadastro não respondeu agora. Tarefa
     * não tem projeção por cliente (o alvo dela não é projetado), então a lista
     * vem VAZIA e marcada, em vez de vazia e calada. Cliente sem ficha no
     * cadastro, ou organização sem cadastro ligado, é `false`: não há tarefa.
     */
    stale: z.boolean(),
  }),

  /**
   * As pessoas da equipe que escreveram para o cliente nesta conversa (#641),
   * da que falou por último para a primeira. Nota interna não conta: ela não
   * chega ao cliente.
   *
   * Sai de quem apertou enviar no CliqCRM. Mensagem enviada por fora, direto
   * no atendimento, não diz a pessoa, e não entra.
   */
  participantes: z.array(
    z.object({
      usuario_id: IdSchema,
      nome: z.string().nullable(),
      foto_url: z.string().nullable(),
    }),
  ),

  mensagens: z.object({
    itens: z.array(
      z.object({
        /** A projeção não cunha id por mensagem (decisão da C19) — a referência externa é a identidade. */
        referencia_externa: ReferenciaExternaSchema,
        direcao: z.enum(['entrada', 'saida', 'sistema']),
        /** `interna` NUNCA pode vazar para o canal — a tela bloqueia por este campo (A07 C17). */
        visibilidade: z.enum(['publica', 'interna']),
        conteudo: z.string().nullable(),
        /**
         * Os nomes que a nota interna mencionou (#569), para a tela destacar
         * cada "@Nome" no texto. Vazio em mensagem sem menção.
         */
        mencoes: z.array(z.string()).optional(),
        /**
         * Os anexos, com URL ASSINADA e curta (C02).
         *
         * Nunca a `data_url` do motor: a A02 §4.2 mediu que ela é pública e não
         * expira, então repassá-la faria de qualquer vazamento de resposta um
         * vazamento de arquivo. A assinatura é gerada a cada leitura — o que a
         * projeção guarda é só a identidade do arquivo.
         *
         * `expiraEm` viaja junto para a tela poder avisar, em vez de mostrar um
         * link que morre calado.
         */
        anexos: z.array(AnexoSchema),
        /**
         * O menu de opções que a mensagem levou (#1012), para a tela mostrar o
         * que o cliente recebeu: no WhatsApp, botões ou lista. Ausente ou nulo
         * em mensagem que não é menu.
         */
        menu: z
          .object({ opcoes: z.array(z.object({ id: z.string(), titulo: z.string() })) })
          .nullable()
          .optional(),
        enviada_em: InstanteSchema,
        autor: z.object({
          tipo: z.enum(['cliente', 'agente', 'sistema']),
          nome: z.string().nullable(),
          /**
           * A foto de perfil (061) de quem da equipe enviou, pela autoria
           * gravada no envio. Nula para o cliente, para o sistema e para o que
           * foi enviado de dentro do atendimento: a tela mostra as iniciais.
           */
          foto_url: z.string().nullable(),
        }),
        /**
         * Só em mensagem de saída (A07 §4.3). `nao_suportado` é primeira
         * classe: canal que não confirma nunca vira ícone de falha (§8.2).
         */
        confirmacao_entrega: z
          .object({
            estado: z.enum([
              'aguardando',
              'confirmado',
              'lido',
              'falhou',
              'nao_suportado',
              'desconhecido',
            ]),
            suporte: z.enum(['suportado', 'nao_suportado', 'desconhecido']),
            falha: z.object({ motivo: z.string(), acao: z.string() }).nullable(),
          })
          .nullable(),
      }),
    ),
    pagina: z.object({
      /** Cursor opaco (§6.1) para as mensagens MAIS ANTIGAS — abertura progressiva da §11.2. */
      cursor_anterior: z.string().nullable(),
      tem_mais_antigas: z.boolean(),
    }),
    projetado_em: InstanteSchema.nullable(),
  }),

  /**
   * O `CapabilidadesDeCanal` da C03, como projetado em `read.inboxes` (C19)
   * — estrutura evolui com a matriz da A03, não com este contrato. `null`
   * quando o inbox ainda não foi projetado: a tela cai para o editor
   * mínimo (A07 C38).
   */
  capabilities: z.record(z.string(), z.unknown()).nullable(),
});

export type ConversaComposta = z.infer<typeof ConversaCompostaSchema>;

/**
 * Iniciar atendimento com quem nunca escreveu (C31).
 *
 * ── Por que isto existe, e por que só com template ────────────────────────
 *
 * Cliente cadastrado à mão — pelo portal, por telefone, por importação — não
 * tem conversa, e até aqui não havia por onde falar com ele: toda conversa
 * nascia de mensagem RECEBIDA. A tela mostrava "Abrir conversa" desabilitado e
 * pronto.
 *
 * O WhatsApp não permite texto livre para quem não escreveu nas últimas 24
 * horas. Só modelo aprovado sai — e por isso o template não é uma opção deste
 * comando, é o corpo dele. Aceitar texto livre aqui produziria a recusa
 * silenciosa do provedor, que é o modo de falha que a §10.2 existe para evitar.
 *
 * ── A caixa vai no pedido, e não é deduzida ───────────────────────────────
 *
 * A revenda pode ter mais de um número. Escolher "o primeiro conectado" faria
 * a primeira mensagem sair pelo número de pós-venda quando a intenção era
 * vendas — e o cliente responderia para lá. Quem sabe é quem manda.
 */
export const IniciarAtendimentoSchema = z
  .object({
    /** A caixa de entrada do canal, como referência da engine (§3.2). */
    inbox_referencia_externa: z.string().min(1),
    template: z.object({
      nome: z.string().min(1),
      /**
       * O par nome + idioma é que identifica o template.
       *
       * Ver o cabeçalho de `porta-envio.ts`: a Meta permite o mesmo nome em
       * idiomas diferentes, e mandar só o nome faz o motor montar a chamada
       * sem `language.code` — que volta como erro de parâmetro, sobre um
       * template que estava aprovado.
       */
      idioma: z.string().min(1),
      /**
       * Um valor por `{{n}}` do corpo, na ordem.
       *
       * O catálogo diz quantas variáveis o template usa; mandar menos produz
       * recusa do provedor, e mandar mais é ignorado em silêncio — as duas
       * chegam ao cliente como mensagem que não saiu.
       */
      valores: z.array(z.string()),
    }),
  })
  .strict();

export type IniciarAtendimento = z.infer<typeof IniciarAtendimentoSchema>;

export const AtendimentoIniciadoSchema = z
  .object({
    /** A conversa na engine. É o que existe no instante da resposta. */
    conversa_referencia_externa: z.string(),
    /**
     * O canônico, quando já cunhado — e `null` é o normal aqui.
     *
     * A cunhagem (C13) acontece na INGESTÃO, quando o evento da conversa nova
     * volta pelo webhook. Prometer o canônico na resposta obrigaria a criar o
     * vínculo por um segundo caminho, e aí duas partes do sistema cunhariam a
     * mesma conversa.
     *
     * A tela trata isso esperando: a conversa aparece na fila em segundos.
     */
    conversa_id: IdSchema.nullable(),
    /** A mensagem do template, como a engine a registrou. */
    mensagem_referencia_externa: z.string(),
  })
  .strict();

export type AtendimentoIniciado = z.infer<typeof AtendimentoIniciadoSchema>;
